
  import { defineComponent } from 'vue';

  import constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import { TStorePaths } from '@/common/types/storePaths';

  import AggregatedPageWrapper from '../components/common/AggregatedPageWrapper.vue';
  import { TExportConfig } from '../components/common/ved-aggregated-export-buttons/types';
  import ForeignTradeByRegionsBaseInfo from '../components/foreign-trade-by-regions/ForeignTradeByRegionsBaseInfo.vue';
  import ForeignTradeByRegionsFilter from '../components/foreign-trade-by-regions/ForeignTradeByRegionsFilter.vue';
  import ForeignTradeByRegionsTable from '../components/foreign-trade-by-regions/ForeignTradeByRegionsTable.vue';
  import {
    FOREIGN_TRADE_BY_REGIONS_ACTIONS_NAME,
    FOREIGN_TRADE_BY_REGIONS_GETTERS_NAME,
    FOREIGN_TRADE_BY_REGIONS_MUTATIONS_NAME,
  } from '../store/foreignTradeByRegions/name';
  import { IndicatorTypes, ProductValues, SelectTypes, TreeCountryValues } from '../types/common-aggregated';
  import type { TForeignTradeByRegionsFilter } from '../types/foreign-trade-by-regions';
  import { TAggregatedPermissions } from '../types/permission';
  import { getFilenameWithPeriod } from '../utils/filename';

  import links from '@/utils/links';

  const DEFAULT_FILTER: TForeignTradeByRegionsFilter = {
    periods: ['2007', '2008', '2009'],
    countries: [TreeCountryValues.CANADA_99],
    productCodes: [ProductValues.ALL_PRODUCTS],
    selectTypeId: SelectTypes.CUMULATIVE_TOTAL,
    indicatorTypeId: IndicatorTypes.PRICE_AND_WEIGHT,
  };

  export default defineComponent({
    name: 'ForeignTradeByRegions',
    components: {
      AggregatedPageWrapper,
      ForeignTradeByRegionsFilter,
      ForeignTradeByRegionsBaseInfo,
      ForeignTradeByRegionsTable,
    },
    mixins: [security],
    data(): {
      filter: TForeignTradeByRegionsFilter;
      filterForExport: TForeignTradeByRegionsFilter;
      defaultFilter: TForeignTradeByRegionsFilter;
      storePaths: TStorePaths;
      productText: Optional<string>;
    } {
      return {
        filter: { ...DEFAULT_FILTER },
        filterForExport: { ...DEFAULT_FILTER },
        defaultFilter: { ...DEFAULT_FILTER },
        storePaths: {
          action: FOREIGN_TRADE_BY_REGIONS_ACTIONS_NAME.searchRegionAggregated,
          getter: FOREIGN_TRADE_BY_REGIONS_GETTERS_NAME.getRegionAggregated,
          mutation: FOREIGN_TRADE_BY_REGIONS_MUTATIONS_NAME.setRegionAggregated,
        },
        productText: undefined,
      };
    },
    mounted() {
      this.checkPermissionAndRedirect(this.getPermissions.view);
    },
    computed: {
      getPermissions(): TAggregatedPermissions {
        return {
          view: constants.Permissions.ForeignTradeAnalyticsWorldByRegionsView,
          build: constants.Permissions.ForeignTradeAnalyticsWorldByRegionsBuild,
          export: constants.Permissions.ForeignTradeAnalyticsWorldByRegionsDownload,
        };
      },

      getExportConfig(): TExportConfig {
        return {
          pdfFileName: this.getFileName(this.filter),
          excelFileName: this.getFileName(this.filterForExport),
          excel: {
            permissionCode: this.getPermissions.export,
            url: links.ved.foreignTrade.regions.exportExcel,
            params: this.filterForExport,
          },
        };
      },
    },
    methods: {
      searchFilter(filter: Partial<TForeignTradeByRegionsFilter>) {
        this.filter = { ...this.filter, ...filter };
      },
      updateFilter(filter: Partial<TForeignTradeByRegionsFilter>) {
        this.filterForExport = { ...this.filterForExport, ...filter };
      },
      setProductText(newValue: string) {
        this.productText = newValue;
      },
      getFileName(filter: TForeignTradeByRegionsFilter) {
        return getFilenameWithPeriod('Внешняя_торговля_со_странами_мира_в_разрезе_субъектов_РФ', filter.periods);
      },
    },
  });
